import {
  Button,
  FieldGroup,
  FieldRow,
  FieldSet,
  Input,
  ModalDialog,
  Section,
  SelectBox,
} from "react-corner-ds";
import style from "./PersonalDataAdditional.module.css";
import { Trans, useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, FieldError, useForm } from "react-hook-form";
import { PersonalDataAdditionalInfo } from "../../../models/request/data/data.model";
import {
  CountriesResponse,
  CountryItem,
} from "../../../models/response/response.model";
import i18n from "../../../i18n";
import { Request } from "../../../models/request/request.model";
import { reformatDate, workingAge } from "../../../utils/reformat";
import { useFlow } from "../../../hooks/useFlow/useFlow";
import { definePatternByName } from "../../../utils/constants";
import { store } from "../../../redux/store";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import moment from "moment";
import "moment/locale/fr";
import "moment/locale/it";
import "moment/locale/de";
import "moment/locale/en-gb";
import { maritalItems } from "../../../assets/locales/utils";
import { Bank } from "../../../models/bank/bank.model";
import { Customer } from "../../../models/bank/customer/customer.model";

interface IPersonalDataAdditional {
  modalRecap?: boolean;
  modalCallback?: () => void;
  mockObject?: Bank;
}

const PersonalDataAdditional: React.FC<IPersonalDataAdditional> = ({
  modalRecap,
  modalCallback,
  mockObject,
}) => {
  const { t } = useTranslation();
  const [countries, setCountries] = useState<CountryItem[]>([]);
  const [isOpenCalendar, setIsOpenCalendar] = useState<boolean>(false);
  const [isWorkingAgeValid, setIsWorkingAgeValid] = useState<
    boolean | undefined
  >(undefined);
  const [nextStepLoading, setNextStepLoading] = useState<boolean>(false);
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const [isAvsValid, setIsAvsValid] = useState<boolean>(false);
  const [avs, setAvs] = useState<string>("");

  const { callSteploader } = useFlow();

  const items = useMemo(() => {
    return countries.map((item: CountryItem) => ({
      label: [item.name],
      value: item.alpha_2,
      icon: item.alpha_2,
    }));
  }, [countries]);

  const searchCountry = useCallback(
    (country: string) => {
      return items.find((element) => {
        return element.value === country;
      });
    },
    [items]
  );

  // disable future dates
  const today = moment();
  const disableFutureDt = useCallback(
    (current: any) => {
      return current.isBefore(today);
    },
    [today]
  );

  const { handleSubmit, formState, control, setValue, trigger } =
    useForm<PersonalDataAdditionalInfo>({
      mode: "all",
      reValidateMode: "onChange",
      defaultValues: {
        dateOfBirth: "",
        placeOfBirth: "",
        maritalStatus: "",
        nationality: "",
        socialSecurityNumber: "",
      },
    });

  const collectedDataValidation = useCallback((store: Customer) => {
    let triggerValidation =
      store.DateOfBirth &&
      store.PlaceOfBirth &&
      store.Nationality &&
      store.MaritalStatus &&
      store.SocialSecurityNumber;
    return triggerValidation;
  }, []);

  const submitHandler = async (data: PersonalDataAdditionalInfo) => {
    let body: Request = {
      Data: {
        FlowDirection: modalRecap ? "Still" : "Next",
        Bank: {
          Customer: {
            DateOfBirth: reformatDate(
              data.dateOfBirth,
              "DD/MM/YYYYY",
              "YYYYMMDD"
            ),
            PlaceOfBirth: data.placeOfBirth,
            Nationality: data.nationality,
            MaritalStatus: data.maritalStatus,
            SocialSecurityNumber: data.socialSecurityNumber,
          },
        },
      },
      Extra: {},
    };

    setNextStepLoading(true);
    if (modalRecap) {
      await callSteploader(body, false, "PersonalDataAdditional");
      setNextStepLoading(false);
    } else {
      await callSteploader(body);
    }
    modalRecap && modalCallback && modalCallback();
  };

  const hideModal = useCallback((ev: any) => {
    ev.preventDefault();
    setIsShowModal(false);
  }, []);

  const validity = useCallback(
    (error: FieldError | undefined, isTouched: boolean) =>
      !error && isAvsValid ? true : undefined,
    [isAvsValid]
  );

  const buildAvsModal = useMemo(() => {
    return (
      <div>
        <h1>{t`screens.personalDataAdditional.avsModal.title`}</h1>
        <div>
          <p>
            <Trans
              i18nKey={t`screens.personalDataAdditional.avsModal.description`}
            />
          </p>
        </div>
        <div>
          <Button
            customCssClass={style.understoodButton}
            text={t`screens.personalDataAdditional.avsModal.understood`}
            onClick={() => setIsShowModal(false)}
          />
        </div>
      </div>
    );
  }, [t]);

  useEffect(() => {
    let customerStore: Customer = mockObject?.Customer ?? {};

    if (store.getState().appConf.workflowEnabled) {
      customerStore = store.getState().pega.Data?.Bank.Customer ?? {};
    }

    if (customerStore) {
      customerStore?.DateOfBirth &&
        setValue(
          "dateOfBirth",
          reformatDate(customerStore.DateOfBirth, "YYYYMMDD", "DD/MM/YYYY")
        );
      setValue("placeOfBirth", customerStore?.PlaceOfBirth ?? "");
      setValue("nationality", customerStore?.Nationality ?? "");
      setValue("maritalStatus", customerStore?.MaritalStatus ?? "");
      setValue(
        "socialSecurityNumber",
        customerStore?.SocialSecurityNumber ?? ""
      );
      setAvs(customerStore?.SocialSecurityNumber ?? "");
      setIsAvsValid(customerStore?.SocialSecurityNumber ? true : false);
      //Trigger validation if there's customer data coming from pega
      if (collectedDataValidation(customerStore))
        setTimeout(() => {
          trigger();
        }, 250);
    }
  }, [mockObject?.Customer, setValue, trigger, collectedDataValidation]);

  useEffect(() => {
    import(`../../../assets/countries/${i18n.resolvedLanguage}.json`).then(
      (resp: CountriesResponse) => {
        setCountries(resp.countries);
      }
    );
  }, [i18n.resolvedLanguage]);

  const buildCountrySelectBox = useCallback(
    (name_: any, label: string, disabled = false) => (
      <Controller
        control={control}
        name={name_}
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
        }}
        render={({
          field: { onChange, name, value, onBlur },
          fieldState: { error },
        }) => (
          <div onClick={() => setIsOpenCalendar(false)}>
            {disabled ? (
              <Input
                disabled
                label={label}
                value={searchCountry(value)?.label[0] || ""}
                type="text"
                customCssClass={style.disabledInput}
              />
            ) : (
              <SelectBox
                label={label}
                items={items}
                name={name}
                value={value}
                onChange={(item: any) => {
                  onChange(item.value);
                }}
                onBlur={onBlur}
                invalid={error?.message ? true : false}
                errorMsg={error?.message}
              />
            )}
          </div>
        )}
      />
    ),
    [control, items, t]
  );

  const birthDateInput = useMemo(
    () => (
      <Controller
        control={control}
        name={"dateOfBirth"}
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
          pattern: {
            value: definePatternByName("DateOfBirth"),
            message: t`errors.input.invalidPattern`,
          },
          validate: (e) =>
            workingAge(
              typeof e === "string"
                ? e.toString()
                : moment(e, "DD/MM/YYYY", true).format("DD/MM/YYYY")
            ),
        }}
        render={({
          field: { name, onChange, value, onBlur },
          fieldState: { error },
        }) => (
          <div
            onClick={() =>
              !isOpenCalendar &&
              !store.getState().pega?.Data?.Bank?.Customer?.KYC &&
              setIsOpenCalendar(true)
            }
            className={style.customInputCalendar}
          >
            <Input
              name={name}
              label={t`screens.personalDataAdditional.birthDate` || ""}
              customCssClass={`${style.customInputCalendar} ${
                store.getState().pega?.Data?.Bank?.Customer?.KYC
                  ? style.disabledInput
                  : ""
              }`}
              valid={
                !isOpenCalendar &&
                isWorkingAgeValid !== undefined &&
                !isWorkingAgeValid
                  ? false
                  : true
              }
              onBlur={onBlur}
              errorMsg={
                !isOpenCalendar
                  ? error?.message || t`screens.common.errorDate` || ""
                  : ""
              }
              maxLength={0}
              onFocus={() => !value && setIsWorkingAgeValid(false)}
              children={
                <>
                  <Datetime
                    value={value}
                    inputProps={{
                      placeholder: "dd/mm/yyyy",
                      maxLength: 10,
                      minLength: 10,
                      className: style.customInput,
                      disabled:
                        store.getState().pega?.Data?.Bank?.Customer?.KYC,
                      onFocus: () => !value && setIsWorkingAgeValid(false),
                    }}
                    onChange={(e: any) => {
                      onChange(e);
                      setIsOpenCalendar(false);

                      workingAge(
                        typeof e === "string"
                          ? e.toString()
                          : moment(e, "DD/MM/YYYY", true).format("DD/MM/YYYY")
                      )
                        ? setIsWorkingAgeValid(true)
                        : setIsWorkingAgeValid(false);
                    }}
                    dateFormat="DD/MM/YYYY"
                    timeFormat={false}
                    closeOnClickOutside={true}
                    closeOnSelect={true}
                    className={
                      isOpenCalendar
                        ? `${style.customCalendar} rdtOpen`
                        : style.customCalendar
                    }
                    locale={i18n.resolvedLanguage}
                    isValidDate={disableFutureDt}
                    initialViewDate={value || new Date(1980, 0, 1)}
                  />
                  <i
                    className="cticonapp-calendar"
                    onClick={() => setIsOpenCalendar(!isOpenCalendar)}
                  />
                </>
              }
            />
          </div>
        )}
      />
    ),
    [control, disableFutureDt, isWorkingAgeValid, isOpenCalendar, t]
  );

  const maritalStatusSelectBox = useMemo(
    () => (
      <Controller
        control={control}
        name={"maritalStatus"}
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
        }}
        render={({
          field: { onChange, name, value, onBlur },
          fieldState: { error },
        }) => (
          <div onClick={() => setIsOpenCalendar(false)}>
            <SelectBox
              name={name}
              label={t`screens.personalDataAdditional.maritalStatus` || ""}
              items={maritalItems(t)}
              value={value}
              onChange={(item: any) => {
                onChange(item.value);
              }}
              onBlur={onBlur}
              invalid={error?.message ? true : false}
              errorMsg={error?.message}
            />
          </div>
        )}
      />
    ),
    [control, t]
  );

  const handleChange = useCallback((avsValue: string) => {
    const avsNumber = avsValue
      .replace(/\D/g, "")
      .match(/(\d{0,3})(\d{0,4})(\d{0,4})(\d{0,2})/);

    if (avsNumber) {
      avsValue = !avsNumber[2]
        ? avsNumber[1]
        : `${avsNumber[1]}.${avsNumber[2]}${`${
            avsNumber[3] ? `.${avsNumber[3]}` : ""
          }`}${`${avsNumber[4] ? `.${avsNumber[4]}` : ""}`}`;

      setAvs(avsValue);
      setIsAvsValid(avsValue.length === 16);
    }
  }, []);

  const avsInput = useMemo(
    () => (
      <Controller
        control={control}
        name="socialSecurityNumber"
        defaultValue={""}
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
          pattern: {
            value: definePatternByName("Number"),
            message: t`errors.input.invalidPattern`,
          },
          minLength: 16,
        }}
        render={({
          field: { onChange, onBlur, name, value },
          fieldState: { isTouched, error },
        }) => (
          <div onClick={() => setIsOpenCalendar(false)}>
            <Input
              name={name}
              label={t`screens.personalDataAdditional.avsNumber` || ""}
              onChange={(e) => {
                onChange(e.target.value);
                handleChange(e.target.value);
              }}
              onBlur={onBlur}
              valid={error ? false : validity(error, isTouched)}
              errorMsg={
                error?.message ? error?.message : t`errors.input.invalidPattern`
              }
              value={avs}
              maxLength={16}
              placeholder="000.0000.0000.00"
            />
          </div>
        )}
      />
    ),
    [control, t, avs, validity, handleChange]
  );

  return (
    <div className="container">
      <h1>{t`screens.personalDataAdditional.pageTitle`}</h1>
      <form
        autoComplete="off"
        onSubmit={handleSubmit(submitHandler)}
        className={modalRecap ? style.modalView : ""}
      >
        <Section>
          <FieldSet>
            <FieldRow>
              <FieldGroup>{birthDateInput}</FieldGroup>
              <FieldGroup>
                {buildCountrySelectBox(
                  "placeOfBirth",
                  t`screens.personalDataAdditional.birthCountry`,
                  store.getState().pega?.Data?.Bank?.Customer?.KYC
                )}
              </FieldGroup>
            </FieldRow>

            <FieldRow>
              <FieldGroup>
                {buildCountrySelectBox(
                  "nationality",
                  t`screens.personalDataAdditional.nationality`,
                  store.getState().pega?.Data?.Bank?.Customer?.KYC
                )}
              </FieldGroup>
              <FieldGroup>{maritalStatusSelectBox}</FieldGroup>
            </FieldRow>
            <hr />
            <p>
              <b>{t`screens.personalDataAdditional.avs`}</b>
            </p>
            <Trans
              i18nKey={t`screens.personalDataAdditional.avsInfo`}
              components={{
                AvsInfo: (
                  <button
                    type="button"
                    className={style.RadioInfoButton}
                    onClick={() => setIsShowModal(true)}
                  />
                ),
              }}
            />
            <FieldRow>
              <FieldGroup>{avsInput}</FieldGroup>
            </FieldRow>
          </FieldSet>
        </Section>

        <Section>
          <FieldSet>
            <div className={style.btnHolder + " btnHolder"}>
              <Button
                disabled={!formState.isValid || nextStepLoading}
                text={t`screens.common.continue`}
                type="submit"
                icon={
                  nextStepLoading
                    ? { icon: "loader", position: "right", spin: true }
                    : undefined
                }
              />
            </div>
          </FieldSet>
        </Section>
      </form>

      <ModalDialog
        id={"infoModal"}
        show={isShowModal}
        handleClose={hideModal}
        backdropClose={true}
        customCssClass={style.genericModal}
      >
        {buildAvsModal}
      </ModalDialog>
    </div>
  );
};

export default PersonalDataAdditional;
